// src/utils/permissions.js
// Single source of truth for the permission catalog and the helpers that gate
// the UI. The backend (gp_roles) stores each role's permission keys; the logged
// in user carries their resolved list in auth.user.permissions. The admin role
// is always treated as a superuser so it can never lock itself out.

// Catalog grouped by module — drives the Role editor checkboxes.
export const PERMISSION_GROUPS = [
  { module: 'Dashboard', perms: [
    { key: 'dashboard.view', label: 'View dashboard' },
  ] },
  { module: 'EOI Submissions', perms: [
    { key: 'submissions.view',   label: 'View submissions' },
    { key: 'submissions.export', label: 'Export (PDF / Excel)' },
    { key: 'submissions.status', label: 'Change status' },
    { key: 'submissions.delete', label: 'Delete submissions' },
  ] },
  { module: 'Projects', perms: [
    { key: 'projects.view',   label: 'View projects' },
    { key: 'projects.create', label: 'Add projects' },
    { key: 'projects.edit',   label: 'Edit / activate projects' },
    { key: 'projects.delete', label: 'Delete projects' },
  ] },
  { module: 'User Management', perms: [
    { key: 'users.view',   label: 'View users' },
    { key: 'users.create', label: 'Add users' },
    { key: 'users.edit',   label: 'Edit / activate users' },
    { key: 'users.delete', label: 'Delete users' },
  ] },
  { module: 'Roles & Access', perms: [
    { key: 'roles.view',   label: 'View roles' },
    { key: 'roles.create', label: 'Add roles' },
    { key: 'roles.edit',   label: 'Edit roles' },
    { key: 'roles.delete', label: 'Delete roles' },
  ] },
  // NOTE: keys are stored in gp_roles — only the labels were rebranded from
  // "Visitors" to "Customer Site Visit"; never rename the keys.
  { module: 'Customer Site Visit', perms: [
    { key: 'visitors.view',           label: 'Site Visit Module' },
    { key: 'visitor_dashboard.view',  label: 'Site Visit Dashboard' },
    { key: 'visitors.export',         label: 'Export Site Visit Records' },
    { key: 'visitors.edit',           label: 'Customer Site Visit Record (By Sales Team)' },
    { key: 'visitors.assign',         label: 'Assign Site Visit to Other Sales Person' },
    { key: 'visitors.history',        label: 'Site Visit History (per-record activity log)' },
  ] },
];

export const ALL_PERMISSIONS = PERMISSION_GROUPS.flatMap(g => g.perms.map(p => p.key));

// Page key -> permission required to see it (sidebar + routing).
export const PAGE_PERMISSION = {
  dashboard:        'dashboard.view',
  submissions:      'submissions.view',
  projects:         'projects.view',
  users:            'users.view',
  settings:         'settings.view',
  roles:            'roles.view',
  visitors:         'visitors.view',
  visitor_dashboard:'visitor_dashboard.view',
};

// Resolve a user's effective permission list. Admin is always a superuser.
export function permsOf(user) {
  if (!user) return [];
  if (user.role === 'admin') return ALL_PERMISSIONS;
  return Array.isArray(user.permissions) ? user.permissions : [];
}

// Does this user hold a given permission?
export function can(user, perm) {
  if (!user || !perm) return false;
  if (user.role === 'admin') return true;
  return Array.isArray(user.permissions) && user.permissions.includes(perm);
}
